import { of } from 'rxjs';
import { Account } from '@infodocs/account/interfaces/account';
import { Company } from '@infodocs/company/interfaces/company';
import { Officer, OfficerOption } from '@infodocs/company/interfaces/officer';
import {
	CompanyType,
	PaymentType,
	PermissionId,
	StatusId,
} from '@infodocs/company/variables/enums';
import { Trust } from '@infodocs/trust/interfaces/trust';

export const DEMO_COMPANY_ID = 1;
export const DEMO_TRUST_ID = 1;
export const DEMO_ACCOUNT_ID = 1;

export const DEMO_COMPANY = {
	id: DEMO_COMPANY_ID,
	company_id: DEMO_COMPANY_ID,
	id_text: 'demo-company',
	company_type: 'Private Company',
	company_type_id: CompanyType.private_company,
	company_type_short_code: 'Pty',
	company_name: 'Demo Company (Pty) Ltd',
	registration_number: '2020/123456/07',
	trade_name: 'Demo Co',
	prev_company_name: '',
	old_registration_number: '',
	business_start_date: '2020-01-15',
	business_description: 'Software and compliance services',
	industry: 'Information Technology',
	cipc_status: 'In Business',
	compliance_status: 'compliant',
	phone: '0115551234',
	email: 'demo@infodocs.co.za',
	website: 'https://infodocs.co.za',
	principal_contact_name: 'Jane Demo',
	principal_contact_phone: '0825551234',
	principal_contact_email: 'jane@demo.co.za',
	tax_number: '9123456789',
	paye_number: '',
	sdl_number: '',
	uif_number: '',
	uif_registration_number: '',
	vat_number: '4123456789',
	vat_category: '',
	compensation_number: '',
	public_interest: 0,
	bbbee_level: '4',
	incorporation_date: '2020-01-15',
	financial_year_end: 2,
	physical_address_1: '1 Demo Street',
	physical_address_2: '',
	physical_city: 'Johannesburg',
	physical_province: 'Gauteng',
	physical_postal_code: '2001',
	physical_country_id: 250,
	pos_address_1: 'PO Box 1',
	pos_address_2: '',
	pos_city: 'Johannesburg',
	pos_province: 'Gauteng',
	pos_postal_code: '2000',
	pos_country_id: 250,
	bank_details: '',
	notes: '',
	authorised_shares: 1000,
	total_authorised: 1000,
	total_issued: 100,
	permission_id: PermissionId.edit,
	binary: '1',
	auditor: {} as Company['auditor'],
	is_subscribed: true,
	has_shareholders: true,
	number_of_shareholdings: 1,
	account_id: DEMO_ACCOUNT_ID,
	account_type_id: 2,
	bo_customer_code_enabled: true,
} as Company;

export const DEMO_TRUST = {
	id: DEMO_TRUST_ID,
	trust_id: DEMO_TRUST_ID,
	trust_name: 'Demo Family Trust',
	trust_number: 'IT1234/2020',
	status_id: 1,
	tax_number: '9123456789',
	trust_type_id: 1,
	trust_master_office_id: 1,
	financial_year_end: '02',
	last_afs_approved: '2024-02-28',
	next_afs_due: '2026-02-28',
	id_text: 'demo-trust',
	sdl_number: '',
	uif_number: '',
	master_of_high_court_office: 'Johannesburg',
	account_id: DEMO_ACCOUNT_ID,
	permission_id: PermissionId.edit,
} as Trust;

export const DEMO_ACCOUNT = {
	id: DEMO_ACCOUNT_ID,
	account_id: DEMO_ACCOUNT_ID,
	account_name: 'Demo Account',
	account_type: 'Professional',
	account_type_id: 2,
	account_status_id: 1,
	upgraded_at: '2024-01-01',
	paid_companies: 5,
	card_on_file: true,
	card_brand: 'visa',
	card_last4: '4242',
	card_expiry_date: '12/30',
	customer_code: 'DEMO01',
	customer_code_owner_name: 'Jane Demo',
	customer_code_owner_id: '9001015800088',
	customer_balance: 0,
	invoice_address_1: '1 Demo Street',
	invoice_address_2: '',
	invoice_city: 'Johannesburg',
	invoice_contact: 'Jane Demo',
	invoice_email: 'billing@demo.co.za',
	invoice_name: 'Demo Account',
	invoice_postal_code: '2001',
	payment_option_id: PaymentType.card,
	telephone_number: '0115551234',
	vat_number: '4123456789',
	xero_id: '',
	cipc_customer_id: 12345,
	company_count: 5,
	company_limit: null,
	is_special_offer: false,
	is_account_admin: true,
	has_active_trust_subscription: true,
} as Account;

export const DEMO_OFFICER = {
	id: 10,
	directorship_id: 10,
	individual_id: 10,
	status_id: Number(StatusId.active) || 1,
	first_name: 'Jane',
	second_name: '',
	surname: 'Demo',
	id_number: '9001015800088',
	date_of_birth: '1990-01-01',
	race: '',
	gender: 'F',
	tax_number: '',
	title: 'Ms',
	disability: false,
	country_of_origin: 'South Africa',
	role: 'Director',
	occupation: 'Director',
	appointment_date: '2020-01-15',
	resignation_date: '',
	email: 'jane@demo.co.za',
	phone: '0825551234',
	phone_country_id: 250,
	res_address_1: '1 Demo Street',
	res_address_2: '',
	res_city: 'Johannesburg',
	res_province: 'Gauteng',
	res_postal_code: '2001',
	res_country_id: 250,
	pos_address_1: 'PO Box 1',
	pos_address_2: '',
	pos_city: 'Johannesburg',
	pos_province: 'Gauteng',
	pos_postal_code: '2000',
	pos_country_id: 250,
	company_id: DEMO_COMPANY_ID,
	id_text: 'demo-officer',
} as Officer;

/** Shape expected by officer/director select lists. */
export const DEMO_OFFICER_OPTION = {
	individual_id: '10',
	name: 'Jane Demo',
	id_number: '9001015800088',
	date_of_birth: '1990-01-01',
	citizen: true,
	country_id: true,
	role: 'Director',
	prev_appointment_id: '',
	id_text: 'demo-officer',
} as OfficerOption;

/** Fat body returned for every demo API response. */
export const MODAL_DEMO_HTTP_BODY: Record<string, unknown> = {
	message: 'Demo success',
	json_data: {},
	amount: '25.00',
	cipcFee: '50.00',
	filter: 'name-change',
	name: 'Demo line item',
	status: 'ok',
	submitted: false,
	total_issued: 100,
	total_authorised: 1000,
	company: DEMO_COMPANY,
	trust: DEMO_TRUST,
	account: DEMO_ACCOUNT,
	officer: DEMO_OFFICER,
	id: DEMO_COMPANY_ID,
	company_id: DEMO_COMPANY_ID,
	company_name: DEMO_COMPANY.company_name,
	trust_id: DEMO_TRUST_ID,
	trust_name: DEMO_TRUST.trust_name,
	account_id: DEMO_ACCOUNT_ID,
};


/**
 * Common initialState / public field names used across modals.
 * Applied carefully so FormGroups / Subjects / signals are not overwritten.
 */
export const MODAL_DEMO_INITIAL_STATE: Record<string, unknown> = {
	title: 'Demo modal',
	heading: 'Demo heading',
	body: 'This is dummy content for the modal demo wall.',
	message: 'Are you sure you want to continue with this demo action?',
	confirmLabel: 'Confirm demo',
	fallbackErrorMessage: 'Demo error',
	confirmAction: () => of({ message: 'Demo confirmed' }),
	onSuccess: () => undefined,
	officer: DEMO_OFFICER,
	existingDirector: DEMO_OFFICER,
	companyId: DEMO_COMPANY_ID,
	company: DEMO_COMPANY,
	trust: DEMO_TRUST,
	account: DEMO_ACCOUNT,
	role: 'COMPANY SECRETARY',
	targetAccountId: DEMO_ACCOUNT_ID,
	targetCompanyId: DEMO_COMPANY_ID,
	directorRows: [
		{
			id: 10,
			name: 'Jane Demo',
			email: 'jane@demo.co.za',
			phone: '0825551234',
		},
	],
	transaction: { name: 'Demo transaction' },
	response: { message: 'Demo success' },
	items: [
		{
			name: 'Demo service',
			filter: 'name-change',
			amount: '25',
			cipcFee: '50',
		},
	],
	entryPoint: 'shareholders_tab',
};
